import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import getIsChromatic from 'chromatic/isChromatic';

import { scrubDatadogLog } from '../analytics/scrubDatadogLog';
import { GraphQLTypes } from '../graphqlTypes';

import { StudioConfigEnv, runtimeConfig } from './runtime';

const isChromatic = getIsChromatic();

const isReactCompilerEnabled = process.env.STUDIO_REACT_COMPILER === 'true';

// RUM collects nothing until useDatadogTrackingConsent grants it: logged in
// users, or logged out visitors who allow Performance (counsel, 2026-10-29).
datadogRum.init({
  ...runtimeConfig.dataDogRumOptions,
  trackingConsent: 'not-granted',
});
datadogRum.setGlobalContextProperty(
  'react_compiler_enabled',
  isReactCompilerEnabled,
);

// Initialize Datadog Logs for 100% error capture
// Logs are cheaper than RUM and capture all errors regardless of session sampling
// This is a short term experiment - the hypothesis is that all errors
// in Datadog will be better (than Sentry) because we monitor
// everything else in datadog, so dashboards, alerting, etc will be
// easier in Datadog.
datadogLogs.init({
  clientToken: runtimeConfig.dataDogRumOptions.clientToken,
  site: runtimeConfig.dataDogRumOptions.site,
  service: runtimeConfig.dataDogRumOptions.service,
  env: runtimeConfig.dataDogRumOptions.env,
  forwardErrorsToLogs: true, // Auto-capture console.error
  // Logs is error reporting and runs before consent, like Sentry (counsel,
  // 2026-10-29): its session stays in memory instead of the `_dd_s` cookie,
  // and URLs lose their query strings.
  sessionPersistence: 'memory',
  beforeSend: scrubDatadogLog,
  sessionSampleRate: 100, // 100% capture for logs (not sampled like RUM)
});
datadogLogs.setGlobalContextProperty(
  'react_compiler_enabled',
  isReactCompilerEnabled,
);

const roleOrder = [
  GraphQLTypes.UserPermission.BILLING_MANAGER,
  GraphQLTypes.UserPermission.CONSUMER,
  GraphQLTypes.UserPermission.OBSERVER,
  GraphQLTypes.UserPermission.DOCUMENTER,
  GraphQLTypes.UserPermission.CONTRIBUTOR,
  GraphQLTypes.UserPermission.GRAPH_ADMIN,
  GraphQLTypes.UserPermission.ORG_ADMIN,
] as const;

const graphApiKeyOnlyRoles = [
  GraphQLTypes.UserPermission.LEGACY_GRAPH_KEY,
  GraphQLTypes.UserPermission.PERSISTED_QUERY_PUBLISHER,
] as const;

const orderedGraphRoles = [
  GraphQLTypes.UserPermission.CONSUMER,
  GraphQLTypes.UserPermission.OBSERVER,
  GraphQLTypes.UserPermission.DOCUMENTER,
  GraphQLTypes.UserPermission.CONTRIBUTOR,
  GraphQLTypes.UserPermission.GRAPH_ADMIN,
  // excludes GraphQLTypes.UserPermission.BILLING_MANAGER,
  // excludes GraphQLTypes.UserPermission.ORG_ADMIN,
] as const;

const orderedGraphApiKeyRoles = [
  GraphQLTypes.UserPermission.PERSISTED_QUERY_PUBLISHER,
  GraphQLTypes.UserPermission.CONSUMER,
  GraphQLTypes.UserPermission.OBSERVER,
  GraphQLTypes.UserPermission.DOCUMENTER,
  GraphQLTypes.UserPermission.CONTRIBUTOR,
  GraphQLTypes.UserPermission.GRAPH_ADMIN,
  // excludes GraphQLTypes.UserPermission.BILLING_MANAGER,
  // excludes GraphQLTypes.UserPermission.ORG_ADMIN,
  // excludes GraphQLTypes.LEGACY_GRAPH_KEY for the long-term reason that we do
  // not want to allow new API keys to be LEGACY_GRAPH_KEY.
] as const;

type GraphApiKeyOnlyRole = (typeof graphApiKeyOnlyRoles)[number];
export type GraphApiKeyRoleOption = (typeof orderedGraphApiKeyRoles)[number];
export type UserRoleOption = (typeof roleOrder)[number];

export const Config = {
  isRunForLocalEmbedAuth:
    process.env.LOCAL_EMBED_AUTH_CONTEXT === 'studio-auth' ||
    process.env.LOCAL_EMBED_AUTH_CONTEXT === 'embed',
  apolloLanguageServerVersion:
    process.env.APOLLO_LANGUAGE_SERVER_VERSION ?? 'local',
  sentryEnabled:
    // Capture errors in cypress to make debugging CI easier, we may want to update this to use `isCypressUserEmail` in the future
    !!window.Cypress ||
    [StudioConfigEnv.Prod, StudioConfigEnv.Staging].includes(
      runtimeConfig.env as StudioConfigEnv,
    ),
  settings: runtimeConfig,
  appHost: isChromatic ? 'studio.apollographql.com' : window.location.host,
  gitCommitHash: process.env.GIT_HASH,
  buildTimestamp: Number(process.env.BUILD_TIMESTAMP),
  gaTrackingID: 'G-0BGG5V2W2K',
  service: {
    defaultVariant: 'current',
  },
  absoluteUrl: isChromatic
    ? `https://studio.apollographql.com`
    : `${window.location.protocol}//${window.location.host}/`,
  sessionExpiredUrl: isChromatic
    ? `https://studio.apollographql.com/login?sessionError=1`
    : `${window.location.protocol}//${window.location.host}/login?sessionError=1`,
  loginErrorUrl: isChromatic
    ? `https://studio.apollographql.com/login?loginError=1`
    : `${window.location.protocol}//${window.location.host}/login?loginError=1`,
  docsUrl: 'https://www.apollographql.com/docs',
  communityUrl: 'https://community.apollographql.com',
  federationQuickstartDocsUrl:
    'https://www.apollographql.com/docs/federation/quickstart/',
  pricingUrl: 'https://www.apollographql.com/pricing',
  odysseyUrl: 'https://www.apollographql.com/tutorials/',
  apolloGitHubUrl: 'https://www.github.com/apollographql',
  cloudinaryUrl: 'https://res.cloudinary.com/apollographql/image/upload/',
  blogUrl: 'https://community.apollographql.com/',
  stagingSchemaEndpoint:
    'https://schema-reporting.api.staging.c0.gql.zone/api/graphql',
  platformApiStagingEndpoint: 'https://api-staging.apollographql.com/graphql',
  stagingTracesEndpoint: 'https://usage-reporting.api.staging.c0.gql.zone',
  uplinkStagingEndpoint: 'https://uplink-staging.api.apollographql.com/',
  queryParameters: {
    // Only passed from the embed urls so dev tools opts out of telemetry
    RunTelemetry: 'runTelemetry',
    DownloadAuditExportId: 'downloadAuditExportId',
    Overlay: 'overlay',
    SelectedTimeRange: 'range',
    SelectedTimeRangeFrom: 'from',
    SelectedTimeRangeTo: 'to',
    ActiveReportPaneTab: 'tab',
    SelectedTraceId: 'trace',
    SelectedQueryId: 'query',
    SelectedQueryName: 'queryName',
    SelectedSubgraphName: 'subgraphName',
    QueryListMetricSort: 'metric',
    QueryListMetricFilter: 'metricFilter',
    ClientFilter: 'clients',
    TypeQuery: 'typeQuery',
    SelectedVariant: 'variant',
    ContractVariant: 'contractVariant',
    SourceVariant: 'sourceVariant',
    GraphRef: 'graphRef',
    SelectedDurationBucket: 'traceBucket',
    GraphId: 'graphId',
    Referrer: 'referrer', // referrer is removed from url on page load
    Source: 'utm_source', // utm_source is removed from url on page load
    Campaign: 'utm_campaign', // utm_campaign is removed from url on page load
    Medium: 'utm_medium', // utm_medium is removed from url on page load
    ReferrerDetails: 'referrer_details',
    SessionReferrer: 'session_referrer',
    SessionReferrerCreatedAt: 'session_referrer_created_at',
    SessionReferrerDetails: 'session_referrer_details',
    // Pulsar Insights Page URL params
    CoordinateKind: 'coordinateKind',
    ErrorsBreakdown: 'errorsBreakdown',
    ErrorsDrilldown: 'errorsDrilldown',
    ErrorsGroupBy: 'errorsGroupBy',
    ErrorsCodeFilter: 'errorsCodeFilter',
    ErrorsServiceFilter: 'errorsServiceFilter',
    ErrorsClientNameFilter: 'errorsClientNameFilter',
    ErrorsPathFilter: 'errorsPathFilter',
    ErrorsView: 'errorsView',
    FastModeRange: 'fastModeRange',
    FieldListAfterCursor: 'fieldsAfter',
    FieldListBeforeCursor: 'fieldsBefore',
    FieldListMetricFilter: 'fieldsFilter',
    FieldListMetricSort: 'fieldsSort',
    FieldListSortDirection: 'fieldsDir', // one of 'asc', 'desc'
    FieldListPaginationDirection: 'fieldsPageDir',
    FieldListPage: 'fieldsPage',
    FieldListSearchFilter: 'fieldSearch',
    InsightsTab: 'insightsTab',
    OperationListSortDirection: 'operationsDir', // one of 'asc', 'desc'
    OperationListSearchFilter: 'operationSearch',
    OperationListAfterCursor: 'operationsAfter',
    OperationListBeforeCursor: 'operationsBefore',
    OperationListMetricFilter: 'operationsFilter',
    OperationListMetricFilters: 'operationsFilters',
    OperationListMetricSort: 'operationsSort',
    OperationListPage: 'operationsPage',
    OperationListPaginationDirection: 'operationsPageDir',
    SubgraphListSearchFilter: 'subgraphSearch',
    SubgraphListPage: 'subgraphsPage',
    SubgraphListAfterCursor: 'subgraphsAfter',
    SubgraphListBeforeCursor: 'subgraphsBefore',
    SubgraphListSortDirection: 'subgraphsDir', // one of 'asc', 'desc'
    SubgraphListPaginationDirection: 'subgraphsPageDir',
    SubgraphListMetricFilters: 'subgraphsFilters',
    SubgraphListMetricSort: 'subgraphsSort',
    // Explorer document links
    ExplorerDocument: 'document',
    ExplorerVariables: 'variables',
    ExplorerHeaders: 'headers',
    ExplorerPreflightOperationScript: 'script',
    ExplorerPostflightOperationScript: 'postflightOperationScript',
    EmbeddableExplorerDefaultDocument: 'defaultDocument',
    EmbeddableExplorerDefaultVariables: 'defaultVariables',
    EmbeddableExplorerDefaultHeaders: 'defaultHeaders',
    EmbeddableExplorerDefaultCollectionEntryId: 'defaultCollectionEntryId',
    EmbeddableExplorerDefaultCollectionId: 'defaultCollectionId',
    ExplorerSearchQuery: 'searchQuery',
    ExplorerURLState: 'explorerURLState',
    ExplorerURLIncludeCookies: 'includeCookies',
    ExplorerEndpoint: 'endpoint',
    ExplorerSubscriptionEndpoint: 'subscriptionEndpoint',
    TriggerId: 'triggerId',
    ComparisonId: 'comparisonId',
    LaunchTab: 'tab',
    Token: 'token',
    Email: 'email',
    LoginError: 'loginError',
    LoginErrorMessage: 'loginErrorMessage',
    DisableAutoRedirect: 'disableAutoRedirect',
    AuditExportFrom: 'auditExportFrom',
    AuditExportTo: 'auditExportTo',
    AuditExportUsers: 'auditExportUsers',
    AuditExportGraphs: 'auditExportGraphs',
    CollectionId: 'collectionId',
    FocusCollectionId: 'focusCollectionId',
    SavedCollectionId: 'savedCollectionId',
    SavedCollectionEntryId: 'savedCollectionEntryId',
    SharedCollectionOriginVariantRef: 'sharedCollectionOriginVariantRef',
    PublishSchemaArch: 'publishArch',
    PublishSchemaUpdate: 'publishUpdate',
    PrefillGraphRef: 'prefillGraphRef',
    ShouldPromptForNewSubgraphName: 'shouldPromptForNewSubgraphName',
    NewSubgraphName: 'newSubgraphName',
    ProposalCommentId: 'proposalCommentId',
    CommentThreadId: 'commentThreadId',
    SessionError: 'sessionError',
    SessionErrorMessage: 'sessionErrorMessage',
    State: 'state', // used for OAuth login flow
    PostLogin: 'postLogin',
    SamlCertificateId: 'samlCertificateId',
  } as const,
  cookies: {
    Referrer: 'referrer', // referrer is removed from url on page load
    ReferrerDetails: 'referrer_details',
    SessionReferrer: 'session_referrer',
    SessionReferrerCreatedAt: 'session_referrer_created_at',
    SessionReferrerDetails: 'session_referrer_details',
    Source: 'utm_source', // utm_source is removed from url on page load
    Campaign: 'utm_campaign', // utm_campaign is removed from url on page load
    Medium: 'utm_medium', // utm_medium is removed from url on page load
    HubSpotUtk: 'hubspotutk', // HubSpot tracking cookie
    GoogleClientId: 'tracking_google_client_id', // cookie sent to servers to be added on user create
    UserSegment: 'user_segment', // cookie sent to servers to be added on user create
    LastPage: 'lastPage', // cookie set by the utm grabber package
  } as const,
  metricsTabs: [
    {
      title: 'Usage',
      isAvailableInDemo: true,
      isOperationLevel: true,
      isServiceLevel: true,
      isVisibleOnlyWhenActive: false,
      value: 'usage',
    },
    {
      title: 'Errors',
      isAvailableInDemo: true,
      isOperationLevel: true,
      isServiceLevel: true,
      isVisibleOnlyWhenActive: false,
      value: 'errors',
    },
    {
      title: 'Traces',
      isAvailableInDemo: true,
      isOperationLevel: true,
      isServiceLevel: false,
      isVisibleOnlyWhenActive: false,
      value: 'traces',
    },
  ],
  orgLayoutTabs: {
    Supergraphs: 'Supergraphs',
    ChampionDashboard: 'Dashboard',
    Plan: 'Plan',
    AuditLogs: 'Audit Logs',
    Management: 'Management',
    ApiKeys: 'API Keys',
  },
  settingsSections: {
    inviteLink: '#invite-link',
  },
  modals: {
    // Used by the legacy (flag-off) Launches UI, which still opens the
    // supergraph diff in a modal. The redesigned Launches UI replaced this
    // with an inline Diff tab; remove this entry when that flag is cleaned up.
    supergraphDiffViewer: 'supergraph-diff-viewer',
    configureBuildPipeline: 'configure-build-pipeline',
    contactSalesToUpgrade: 'contact-sales-to-upgrade',
    selfServeUpgradeSuccessful: 'self-serve-upgrade-successful',
    newOrganization: 'new-organization',
    preflightScript: 'preflight-script',
    preflightOperationScript: 'preflight-operation-script',
    postflightOperationScript: 'postflight-operation-script',
    inviteMember: 'invite-member',
    updateBillingEmail: 'update-billing-email',
    updateDisplayName: 'update-display-name',
    updateID: 'update-id',
    updateCompanyUrl: 'update-domain',
    updateDefaultUserRole: 'update-default-user-role',
    updateAccountAvatar: 'update-account-avatar',
    updateGraphAvatar: 'update-graph-avatar',
    updateCreditCard: 'update-credit-card',
    updateUserName: 'update-user-name',
    createGraphApiToken: 'create-graph-api-token',
    newUserKey: 'new-user-key',
    newUserApiKey: 'new-user-api-key',
    updateUserEmail: 'update-user-email',
    updateUserAvatar: 'update-user-avatar',
    createGraph: 'create-graph',
    deleteGraph: 'delete-graph',
    publishYourSchema: 'publish-your-schema',
    endpointAddedConfirmation: 'endpoint-added-confirmation',
    setupMetrics: 'setup-metrics',
    supportRequest: 'support-request',
    queryTraceInspector: 'query-trace-inspector',
    createSlackChannel: 'create-slack-channel',
    createPagerDutyChannel: 'create-pd-channel',
    buildErrors: 'build-errors',
    launchErrors: 'launch-errors',
    configureContractWizard: 'configure-contract-wizard',
    editContractConfigWizard: 'edit-contract-config-wizard',
    viewContractDetails: 'view-contract-details',
    configurePersistedQueriesWizard: 'configure-pq-wizard',
    addNotificationWizard: 'add-notification-wizard',
    configureExcludedClients: 'configure-excluded-clients',
    appChangelog: 'app-changelog',
    addHiddenGraphMember: 'add-hidden-graph-member',
    updateGraphVisibility: 'update-graph-visibility',
    updatePublicVariant: 'update-public-variant',
    deleteGraphVariant: 'delete-graph-variant',
    updateProtectedVariant: 'update-protected-variant',
    createOrgInviteLink: 'create-org-invite-link',
    connectionSettings: 'connection-settings',
    cancelSubscription: 'cancel-subscription',
    cancelSubscriptionFeedback: 'cancel-subscription-feedback',
    reactivateSubscription: 'reactivate-subscription',
    newAuditLogExport: 'audit-export',
    upsertAlert: 'upsert-alert',
    downloadAuditLogExport: 'download-audit-export',
    explorerShareableLinkSettings: 'shareable-link-settings',
    editGraphReadme: 'edit-graph-readme',
    addGraphHomepageLink: 'add-graph-homepage-link',
    embedExplorer: 'embed-explorer',
    embedSandbox: 'embed-sandbox',
    publishSandboxCreateNewGraph: 'sandbox-create-new-graph',
    graphSettingsDescriptionUpdate: 'graph-settings-description-update',
    graphSettingsTitleUpdate: 'graph-settings-title-update',
    operationChecksSettings: 'operation-checks-settings',
    createPersonalOperationCollection: 'create-personal-operation-collection',
    createSharedOperationCollection: 'create-shared-operation-collection',
    deleteOperationCollection: 'delete-operation-collection',
    configureOperationCollection: 'configure-operation-collection',
    viewCollectionEntryDetails: 'view-collection-entry-details',
    deleteOperationCollectionEntry: 'delete-operation-collection-entry',
    saveOperationCollectionEntry: 'save-operation-collection-entry',
    duplicateOperationCollection: 'duplicate-operation-collection',
    renameOperationCollectionEntry: 'rename-operation-collection-entry',
    viewCollectionDetails: 'view-collection-details',
    saveCollectionLoginPrompt: 'save-collection-login-prompt',
    saveMetricsOperationToCollection: 'save-metrics-operation-to-collection',
    cancelPaidSubscription: 'cancel-paid-subscription',
    roverAddSubgraph: 'rover-add-subgraph',
    roverAddVariant: 'rover-add-variant',
    addSubgraph: 'add-subgraph',
    addVariant: 'add-variant',
    deleteSubgraph: 'delete-subgraph',
    editSubgraphRoutingUrl: 'edit-subgraph-routing-url',
    ignoreLinterRuleViolation: 'ignore-linter-rule-violation',
    unignoreLinterRuleViolation: 'unignore-linter-rule-violation',
    editProposalPermissions: 'edit-proposal-permissions',
    editProposalNumberOfApprovers: 'edit-proposal-number-of-approvers',
    proposeSchemaChanges: 'propose-schema-changes',
    addProposalSubgraph: 'add-proposal-subgraph',
    publishRevision: 'publish-revision',
    renameProposalSubgraph: 'rename-proposal-subgraph',
    deleteProposalSubgraph: 'delete-proposal-subgraph',
    editProposalStatus: 'edit-proposal-status',
    deleteProposalComment: 'delete-proposal-comment',
    replaceRequestedReviewers: 'replace-requested-reviewers',
    addReview: 'add-review',
    manageCapacity: 'manage-capacity',
    renameProposalTitle: 'rename-proposal-title',
    deleteOrganizationBlocker: 'delete-organization-blocker',
    setProposalNotificationStatus: 'set-proposal-notification-status',
    setProposalDescriptionTemplate: 'set-proposal-description-template',
    editReApprovalRequirementOnChange: 'edit-re-approval-requirement-on-change',
    editCustomChecksWebhook: 'edit-custom-checks-webhook',
    discardChanges: 'discard-changes',
    operationDetails: 'operation-details',
    uploadNewCertificate: 'upload-new-certificate',
    deleteSamlCertificate: 'delete-saml-certificate',
  } as const,
  roleNameByRoleEnum: {
    // this is our graph admin legacy role for tokens, but we don't want users
    // to feel like they have something wrong by having a legacy key. we expect
    // we'll probably have these "legacy" keys around our system forever, users
    // just won't be able to make new ones
    [GraphQLTypes.UserPermission.LEGACY_GRAPH_KEY]: 'Legacy Admin Key',
    [GraphQLTypes.UserPermission.BILLING_MANAGER]: 'Billing Manager',
    [GraphQLTypes.UserPermission.CONSUMER]: 'Consumer',
    [GraphQLTypes.UserPermission.CONTRIBUTOR]: 'Contributor',
    [GraphQLTypes.UserPermission.DOCUMENTER]: 'Documenter',
    [GraphQLTypes.UserPermission.GRAPH_ADMIN]: 'Graph Admin',
    [GraphQLTypes.UserPermission.OBSERVER]: 'Observer',
    [GraphQLTypes.UserPermission.ORG_ADMIN]: 'Org Admin',
    [GraphQLTypes.UserPermission.PERSISTED_QUERY_PUBLISHER]:
      'Persisted Query Publisher',
  },
  auditStatusByEnum: {
    [GraphQLTypes.AuditStatus.QUEUED]: 'Queued',
    [GraphQLTypes.AuditStatus.CANCELLED]: 'Cancelled',
    [GraphQLTypes.AuditStatus.IN_PROGRESS]: 'In Progress',
    [GraphQLTypes.AuditStatus.COMPLETED]: 'Ready',
    [GraphQLTypes.AuditStatus.FAILED]: 'Failed',
    [GraphQLTypes.AuditStatus.EXPIRED]: 'Expired',
    [GraphQLTypes.AuditStatus.SYNCING]: 'Syncing',
  } as const,
  roleOrder,
  orderedUserOverrideRoles: roleOrder.filter(
    (role) =>
      ![
        GraphQLTypes.UserPermission.BILLING_MANAGER,
        GraphQLTypes.UserPermission.ORG_ADMIN,
      ].includes(role),
  ),
  orderedGraphRoles,
  orderedGraphApiKeyRoles,
  freeRoles: [
    GraphQLTypes.UserPermission.BILLING_MANAGER,
    GraphQLTypes.UserPermission.CONSUMER,
  ],
  roleIsGraphApiKeyOnly(
    p: GraphQLTypes.UserPermission,
  ): p is GraphApiKeyOnlyRole {
    return graphApiKeyOnlyRoles.includes(p as GraphApiKeyOnlyRole);
  },
  roleIsGraphApiKeyOption(
    p: GraphQLTypes.UserPermission | string,
  ): p is GraphApiKeyRoleOption {
    return orderedGraphApiKeyRoles.includes(p as GraphApiKeyRoleOption);
  },
  zenDesksupportTicketPriorities: {
    P0: {
      label: 'Urgent',
      description: 'Service is severely impacted',
    },
    P1: {
      label: 'High',
      description: 'Limited capabilities / unstable with interruptions',
    },
    P2: {
      label: 'Normal',
      description: 'Issue but still able to use critical functionality',
    },
    P3: {
      label: 'Low',
      description: 'Low-to-no service impact',
    },
    // P4 does not exist in Zendesk
    P4: {
      label: 'Low',
      description: 'Low-to-no service impact',
    },
  },
  jsmSupportTicketPriorities: {
    // P0 does not exist in JSM
    P0: {
      label: 'Urgent',
      description: 'Service is severely impacted',
    },
    P1: {
      label: 'Urgent',
      description: 'Service is severely impacted',
    },
    P2: {
      label: 'High',
      description: 'Limited capabilities / unstable with interruptions',
    },
    P3: {
      label: 'Normal',
      description: 'Issue but still able to use critical functionality',
    },
    P4: {
      label: 'Low',
      description: 'Low-to-no service impact',
    },
  },
};

export const FED_NEXT_VERSION: string = 'FED_NEXT';
